<template>
    <div class="slot-machine" :class="{ 'is-running': isRunning }">

        <div class="slot-machine__wrap">

            <div class="slot" v-for="slot in slots" ref="slots">

                <h2 class="slot__title">{{ slot.title }}</h2>
                <div class="slot__window">
                    <div class="slot__wrap">
                        <div class="slot__item" v-for="opt in slot.items"><span>{{ opt }}</span></div>
                        <div class="slot__item slot__item--copy" ><span>{{ slot.items[0] }}</span></div>
                    </div>
                </div>

            </div>

        </div>

        <primary-button @clicked.prevent="start" :loading="opts" class="slot-machine__lever">Draaien!</primary-button>

    </div>
</template>


<script>
import autoValidate from './autoValidateMixin'
import PrimaryButton from 'js/components/elements/PrimaryButton.vue'

const next = window.requestAnimationFrame ||
   window.webkitRequestAnimationFrame ||
   window.mozRequestAnimationFrame ||
   window.msRequestAnimationFrame ||
   window.oRequestAnimationFrame ||
   function(cb) { window.setTimeout(cb, 1000/60) }

export default {
    components: { PrimaryButton },
    mixins: [
        autoValidate
    ],
    data: function() {
        return {
            opts: null,
            startedAt: null,
        }
    },

    computed: {
        slots: function() {
            return this.partial.data.slots
        },
        isRunning: function() {
            return this.opts != null
        }
    },

    methods: {
        flush: function() {
            return Promise.resolve()
        },

        start: function() {

            if (this.isRunning) {
                return
            }

            this.opts = this.slots.map( (data, i) => {

                const slot = this.$refs.slots[i]
                const choice = Math.floor( Math.random() * data.items.length )

                const slotHeight = 140

                const opts = {
                    el: slot.querySelector('.slot__wrap'),
                    finalPos: choice * slotHeight - Math.random() * 15 - 20,
                    startOffset: 2000 + Math.random() * 500 + i * 500,
                    height: data.items.length * slotHeight,
                    duration: 3000 + i * 700, // milliseconds
                    isFinished: false,
                }

                return opts

            })

            const audio = new Audio('/audio/slot-machine.mp3')
            const promise = audio.play()

            if (promise != null) {
                promise.catch(error => {
                    // Auto-play was prevented
                    console.log('No permission to play audio', error)
                })
            }

            next( this.animate )

        },

        animate: function(timestamp) {

            if (this.startedAt == null) {
                this.startedAt = timestamp
            }

            const timeDiff = timestamp - this.startedAt

            this.opts.forEach( opt => {

                if (opt.isFinished) {
                    return
                }

                const timeRemaining = Math.max(opt.duration - timeDiff, 0)
                const power = 3
                const offset = ( Math.pow(timeRemaining, power) / Math.pow(opt.duration, power) ) * opt.startOffset

                // negative, such that slots move from top to bottom
                const pos = -1 * Math.floor((offset + opt.finalPos) % opt.height)

                opt.el.style.transform = 'translateY(' + pos + 'px)'

                if ( timeDiff > opt.duration ) {
                    opt.isFinished = true
                }

            })

            if (this.opts.every( o => o.isFinished )) {
                this.opts = null
                this.startedAt = null
            } else {
                next( this.animate )
            }

        }


    }

}
</script>


<style lang="scss">

    @import 'global';

    .slot-machine {
        text-align: center;

        &__wrap {
            display: flex;
            flex-direction: row;
            justify-content: space-around;
            flex-wrap: wrap;

            @media only screen and (min-width: 1300px) {
                justify-content: center;
                .slot {
                    margin: 0 2%;
                }
            }

        }
    }

    .slot {

        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 140px;

        // fix centering in ie11
        // https://stackoverflow.com/questions/31903734/ie11-flexbox-max-width-and-marginauto
        -ms-flex: 1 0 auto;

        max-width: 200px;
        min-width: 140px;
        margin: 0 5px;

        &__window {
            height: 200px;
            overflow: hidden;

            background: $color-white make-img-url("separator-shade@2x.png") repeat-x top left;
            background-size: 193px 8px;
            border: 1px solid $color-dark;
            border-radius: 4px;

            position: relative;

            &:after {
                content: "";
                position: absolute;
                pointer-events: none;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.1) 7%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.1) 80%, rgba(0,0,0,0.2) 90%, rgba(0,0,0,0.4) 100%);
            }
        }

        &__wrap {
            transform: translateY(-15px);
        }

        &__item {
            height: 140px;
            padding: 10px 20px;

            @include tagify();
            font-size: 1em;

            text-align: center;
            word-break: break-word;

            border-bottom: 1px solid transparentize($color-dark, 0.88);
            border-top: 1px solid transparentize($color-white, 0.05);

            display: flex;
            flex-direction: column;
            justify-content: center;

        }


        &:nth-child(1n) {
            .slot__title, .slot__wrap {
                color: $color-primary;
                text-shadow: 0 0 30px transparentize($color-primary, 0.6);
            }
            .slot__window {
                box-shadow: 0 0 30px transparentize($color-primary, 0.6);
                border-color: $color-primary;
            }
        }

        &:nth-child(2n) {
            .slot__title, .slot__wrap {
                color: $color-secondary;
                text-shadow: 0 0 30px transparentize($color-secondary, 0.6);
                animation-delay: 0.25s;
            }
            .slot__window {
                box-shadow: 0 0 30px transparentize($color-secondary, 0.6);
                border-color: $color-secondary;
                animation-delay: 0.25s;
            }
        }

        &:nth-child(3n) {
            .slot__title, .slot__wrap {
                color: $color-red;
                text-shadow: 0 0 30px transparentize($color-red, 0.6);
                animation-delay: 0.5s;
            }
            .slot__window {
                box-shadow: 0 0 30px transparentize($color-red, 0.6);
                border-color: $color-red;
                animation-delay: 0.5s;
            }
        }

        &:nth-child(4n) {
            .slot__title, .slot__wrap {
                color: darken($color-yellow, 15%);
                text-shadow: 0 0 30px transparentize($color-yellow, 0.6);
                animation-delay: 0.75s;
            }
            .slot__window {
                box-shadow: 0 0 30px transparentize($color-yellow, 0.6);
                border-color: $color-yellow;
                animation-delay: 0.75s;
            }
        }

        .slot__title, .slot__window, .slot__wrap {
            animation: blink-neutral 3s cubic-bezier(.66,.0,.78,.96) alternate infinite;
        }

        .is-running & {
            .slot__title, .slot__window, .slot__wrap {
                animation-duration: 0.5s;
                animation-timing-function: steps(2);
            }
        }


    }

    @keyframes blink-neutral {
        100% {
            color: $color-typography;
            border-color: $color-typography;
            box-shadow: 0 0 0 transparent;
            text-shadow: 0 0 0 transparent;
        }
    }

</style>



// WEBPACK FOOTER //
// SlotMachinePartial.vue?0e15f970